import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Users, TrendingUp, Circle } from "lucide-react";

interface AdvocacyDistribution {
  advocates: number;
  contributors: number;
  passive: number;
  dormant: number;
}

interface EmployeeAdvocacyMatrixProps {
  distribution: AdvocacyDistribution;
  totalEmployees: number;
}

export const EmployeeAdvocacyMatrix = ({ distribution, totalEmployees }: EmployeeAdvocacyMatrixProps) => {
  const getPercentage = (count: number) => {
    return totalEmployees > 0 ? Math.round((count / totalEmployees) * 100) : 0;
  };

  const tiers = [
    {
      name: 'Advocates',
      count: distribution.advocates,
      color: 'hsl(var(--success))',
      bgColor: 'bg-success/10',
      borderColor: 'border-success/30',
      icon: TrendingUp,
      description: 'Active publishers with high engagement',
    },
    {
      name: 'Contributors',
      count: distribution.contributors,
      color: 'hsl(var(--primary))',
      bgColor: 'bg-primary/10',
      borderColor: 'border-primary/30',
      icon: Users,
      description: 'Regular posters with moderate reach',
    },
    {
      name: 'Passive',
      count: distribution.passive,
      color: 'hsl(var(--warning))',
      bgColor: 'bg-warning/10',
      borderColor: 'border-warning/30',
      icon: Circle,
      description: 'Complete profiles, minimal posting',
    },
    {
      name: 'Dormant',
      count: distribution.dormant,
      color: 'hsl(var(--muted-foreground))',
      bgColor: 'bg-muted',
      borderColor: 'border-muted',
      icon: Circle,
      description: 'Incomplete profiles, no activity',
    },
  ];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Users className="h-5 w-5" />
          Employee Advocacy Distribution
        </CardTitle>
        <CardDescription>
          Classification of {totalEmployees} employees by advocacy level
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {tiers.map((tier) => {
            const Icon = tier.icon;
            const percentage = getPercentage(tier.count);
            
            return (
              <div
                key={tier.name}
                className={`p-6 rounded-lg border ${tier.bgColor} ${tier.borderColor} transition-all hover:shadow-md`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Icon className="h-5 w-5" style={{ color: tier.color }} />
                    <h3 className="font-semibold text-lg">{tier.name}</h3>
                  </div>
                  <Badge variant="secondary" className="text-lg font-bold">
                    {tier.count}
                  </Badge>
                </div>
                
                <p className="text-sm text-muted-foreground mb-3">
                  {tier.description}
                </p>
                
                <div className="flex items-center gap-2">
                  <div className="flex-1 h-2 bg-background rounded-full overflow-hidden">
                    <div
                      className="h-full transition-all duration-500"
                      style={{
                        width: `${percentage}%`,
                        backgroundColor: tier.color,
                      }}
                    />
                  </div>
                  <span className="text-sm font-medium" style={{ color: tier.color }}>
                    {percentage}%
                  </span>
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-6 p-4 bg-muted/50 rounded-lg">
          <h4 className="font-semibold mb-2">Advocacy Score Calculation</h4>
          <p className="text-sm text-muted-foreground">
            Score = Profile Completeness (20%) + Posting Frequency (30%) + 
            Avg Engagement (30%) + Company Mentions (20%)
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-3 text-xs">
            <div>
              <span className="font-medium" style={{ color: tiers[0].color }}>Advocates:</span> 80-100
            </div>
            <div>
              <span className="font-medium" style={{ color: tiers[1].color }}>Contributors:</span> 50-79
            </div>
            <div>
              <span className="font-medium" style={{ color: tiers[2].color }}>Passive:</span> 20-49
            </div>
            <div>
              <span className="font-medium" style={{ color: tiers[3].color }}>Dormant:</span> 0-19
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
